/**
 * Excel workbook assembly — extracted from export-excel.ts for Workstream B.
 *
 * Contains sheet ordering, page setup, font normalization, and the
 * buildWorkbook / exportExcel public API. Sheet builders remain in
 * export-excel.ts.
 */

import ExcelJS from "exceljs";
import type { Workbook as ExcelWorkbook } from "exceljs";
import type { Scenario, ModelResult } from "../types";
import { buildDealReport, buildDealExcelWorkbook } from "../deal-report";
import { computeMonthly } from "../monthly";
import { C, NF, normalizeFormulaExpression } from "./shared";
import {
  ALL_IN_COST_LABEL,
  ALL_IN_COST_SHORT_LABEL,
  INSPIRED_REDDAM_CONTRIBUTION_LABEL,
  TOTAL_FUNDING_SOURCES_LABEL,
  GROSS_TOTAL_DEVELOPMENT_COST_LABEL,
  YIELD_ON_COST_LABEL,
  RENT_PCT_REVENUE_SENSITIVITY_HEADER,
  OPERATOR_NPV_LABEL,
  OPERATOR_PAYBACK_LABEL,
  RAMP_RESERVES_REQUIRED_LABEL,
  RENT_PCT_REVENUE_Y1_LABEL,
  Y1_EBITDA_LABEL,
} from "../presentation/return-labels";

import {
  buildAssumptions,
  buildBuildingSchedule,
  buildCover,
  buildBuildings,
  buildDevCost,
  buildDevCashflow,
  buildOpPL,
  buildKPIs,
  buildModelChecks,
  buildRentBridge,
  buildLeaseVsOwnSheet,
  buildDebtSchedule,
  buildInterestSensitivity,
  buildLVRSensitivity,
  buildTwoWaySensitivity,
  buildScenarios,
  buildMonthly,
  buildMonthlyDev,
  buildCensus,
  buildChartsSheet,
  buildBuilderQuotes,
  buildActionItems,
  buildCostAndYield,
} from "../export-excel";

/* ── Sheet ordering ── */
export const SHEET_ORDER = [
  "Cover",
  "Headline KPIs",
  "Charts",
  "Cost & Yield",
  "Assumptions",
  "Building Schedule",
  "Buildings",
  "Op P&L",
  "Dev Cost",
  "Dev Cashflow",
  "Debt Schedule",
  "Monthly",
  "Dev Drawdown",
  "Model Checks",
  "Rent Bridge",
  "Lease vs Own",
  "Interest Sensitivity",
  "LVR Sensitivity",
  "Two-Way Sensitivity",
  "Scenarios",
  "Census",
  "Builder Quotes",
  "Action Items",
];

export const BOARD_SHEET_ORDER = [
  "01 Decision",
  "02 Operator Case",
  "03 Rent & Negotiation",
  "04 Cost & Funding",
  "05 Key Assumptions",
  "06 Downside",
  "07 Lease vs Own",
  "08 Controls & Actions",
  "10 Calc Capacity",
  "11 Calc Op Annual",
  "12 Calc Op Monthly",
  "13 Calc Development",
  "14 Calc Debt",
  "15 Support Evidence",
];

const { Workbook } = ExcelJS;

export function reorderWorksheets(wb: ExcelWorkbook, names: string[]) {
  const nameToWs = new Map(wb.worksheets.map((ws) => [ws.name, ws]));
  for (let i = 0; i < names.length; i++) {
    const ws = nameToWs.get(names[i]);
    if (ws) (ws as any).orderNo = i + 1;
  }
}

/* ── Page setup ── */
const LANDSCAPE_SHEETS = new Set([
  "Cost & Yield",
  "Rent Bridge",
  "Op P&L",
  "Debt Schedule",
  "Monthly",
  "Dev Drawdown",
  "Scenarios",
  "Charts",
  "Builder Quotes",
  "Action Items",
  "02 Operator Case",
  "03 Rent & Negotiation",
  "04 Cost & Funding",
  "06 Downside",
  "07 Lease vs Own",
  "08 Controls & Actions",
  "10 Calc Capacity",
  "11 Calc Op Annual",
  "12 Calc Op Monthly",
  "14 Calc Debt",
  "15 Support Evidence",
]);

const FIT_TO_WIDTH_SHEETS = new Set([
  "Cost & Yield",
  "Rent Bridge",
  "Op P&L",
  "Debt Schedule",
  "Monthly",
  "Dev Drawdown",
  "Scenarios",
  "02 Operator Case",
  "03 Rent & Negotiation",
  "04 Cost & Funding",
  "06 Downside",
  "07 Lease vs Own",
  "08 Controls & Actions",
]);

const PRINT_TITLE_ROWS = new Map<string, string>([
  ["Building Schedule", "1:1"],
  ["Op P&L", "1:1"],
  ["Debt Schedule", "4:4"],
  ["Monthly", "1:1"],
  ["Dev Drawdown", "2:2"],
  ["Rent Bridge", "1:2"],
  ["Scenarios", "3:3"],
  ["Census", "2:2"],
  ["Builder Quotes", "3:3"],
  ["Action Items", "2:2"],
  ["10 Calc Capacity", "1:1"],
  ["11 Calc Op Annual", "1:1"],
  ["12 Calc Op Monthly", "1:1"],
  ["14 Calc Debt", "4:4"],
  ["15 Support Evidence", "2:2"],
]);

const BOARD_PAGE_HEIGHTS = new Map<string, number>([
  ["01 Decision", 1],
  ["02 Operator Case", 1],
  ["03 Rent & Negotiation", 1],
  ["04 Cost & Funding", 2],
  ["05 Key Assumptions", 1],
  ["06 Downside", 1],
  ["07 Lease vs Own", 1],
  ["08 Controls & Actions", 2],
]);

function rowAbsolutePrintArea(printArea: string): string {
  return printArea.replace(
    /\$?([A-Z]+)\$?(\d+)/gi,
    (_match, column: string, row: string) => `${column}$${row}`,
  );
}

function excelColumnName(column: number): string {
  let value = Math.max(1, Math.floor(column));
  let result = "";
  while (value > 0) {
    value -= 1;
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }
  return result;
}

export function applyPageSetup(wb: ExcelWorkbook, scenario: Scenario) {
  // Ampersands are Excel header/footer field introducers; user labels are text.
  const projectName = scenario.assumptions.projectName.replaceAll("&", "&&");
  const scenarioName = scenario.name.replaceAll("&", "&&");
  const dateStr = new Date().toLocaleDateString("en-AU");

  for (const ws of wb.worksheets) {
    const isLandscape = LANDSCAPE_SHEETS.has(ws.name);
    const fitToWidth = FIT_TO_WIDTH_SHEETS.has(ws.name);
    const currentPageSetup = ws.pageSetup ?? {};
    const rowCount =
      ws.rowCount ||
      ((ws as unknown as { rows?: unknown[] }).rows?.length ?? 1);
    const columnCount =
      ws.columnCount ||
      ((ws as unknown as { columns?: unknown[] }).columns?.length ?? 1);
    const printArea = `A1:${excelColumnName(columnCount)}${Math.max(1, rowCount)}`;

    const configuredPrintArea = currentPageSetup.printArea ?? printArea;
    const isBoardSheet = BOARD_PAGE_HEIGHTS.has(ws.name);
    ws.pageSetup = {
      ...currentPageSetup,
      paperSize: 9,
      orientation: isLandscape ? "landscape" : "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: BOARD_PAGE_HEIGHTS.get(ws.name) ?? 0,
      printArea: isBoardSheet
        ? rowAbsolutePrintArea(configuredPrintArea)
        : configuredPrintArea,
      printTitlesRow:
        currentPageSetup.printTitlesRow ?? PRINT_TITLE_ROWS.get(ws.name),
      horizontalCentered: currentPageSetup.horizontalCentered ?? fitToWidth,
      margins: {
        left: 0.25,
        right: 0.25,
        top: 0.5,
        bottom: 0.5,
        header: 0.3,
        footer: 0.3,
      },
      firstPageNumber: 1,
    };
    if (isBoardSheet) {
      (
        ws.pageSetup as ExcelJS.Worksheet["pageSetup"] & {
          useFirstPageNumber?: boolean;
        }
      ).useFirstPageNumber = true;
    }

    // ExcelJS does not preserve rowBreaks when a worker-generated workbook is
    // loaded on the main thread to add charts. Re-establish the intentional
    // Rent Bridge split here because applyPageSetup runs on both sides of that
    // round-trip.
    if (ws.name === "Rent Bridge") {
      let costBuildUpRow = 0;
      for (let row = 1; row <= rowCount; row += 1) {
        if (ws.getCell(row, 1).value === "COST BUILD-UP") {
          costBuildUpRow = row;
          break;
        }
      }
      const breakAfterRow = costBuildUpRow - 1;
      const rowBreaks = (
        ws as unknown as { rowBreaks?: Array<{ id?: number }> }
      ).rowBreaks;
      if (
        breakAfterRow > 0 &&
        !rowBreaks?.some((pageBreak) => pageBreak.id === breakAfterRow)
      ) {
        ws.getRow(breakAfterRow).addPageBreak();
      }
    }
    if (ws.name === "04 Cost & Funding") {
      // The intentional break sits at the end of the scope block, two rows above
      // the SOURCES & USES title in the board column (row 31 on an 8-row
      // register, 31 + overflow on a longer one; loop#036). Re-derive it from
      // the sheet so the worker → main-thread round-trip cannot re-insert a
      // stale row-31 break in the middle of a longer register.
      let sourcesRow = 0;
      for (let row = 1; row <= rowCount; row += 1) {
        if (ws.getCell(row, 10).value === "SOURCES & USES") {
          sourcesRow = row;
          break;
        }
      }
      const breakAfterRow = sourcesRow > 0 ? sourcesRow - 2 : 31;
      const rowBreaks = (
        ws as unknown as { rowBreaks?: Array<{ id?: number }> }
      ).rowBreaks;
      if (!rowBreaks?.some((pageBreak) => pageBreak.id === breakAfterRow)) {
        ws.getRow(breakAfterRow).addPageBreak();
      }
    }

    ws.headerFooter = {
      oddHeader: `&L&8&"Arial"${projectName}&C&8&"Arial"${scenarioName}&R&8&"Arial"${dateStr}`,
      oddFooter: `&L&8&"Arial"TENURE&C&8&"Arial"${isBoardSheet ? `${ws.name.replaceAll("&", "&&")} — page &P of ${BOARD_PAGE_HEIGHTS.get(ws.name)}` : "Support sheet — page &P"}&R&8&"Arial"Confidential`,
    };
  }
}

const BOARD_RENAMES = new Map<string, string>([
  ["Cover", "01 Decision"],
  ["Headline KPIs", "02 Operator Case"],
  ["Rent Bridge", "03 Rent & Negotiation"],
  ["Cost & Yield", "04 Cost & Funding"],
  ["Assumptions", "05 Key Assumptions"],
  ["Two-Way Sensitivity", "06 Downside"],
  ["Lease vs Own", "07 Lease vs Own"],
  ["Model Checks", "08 Controls & Actions"],
  ["Building Schedule", "10 Calc Capacity"],
  ["Op P&L", "11 Calc Op Annual"],
  ["Monthly", "12 Calc Op Monthly"],
  ["Dev Cost", "13 Calc Development"],
  ["Debt Schedule", "14 Calc Debt"],
  ["Census", "15 Support Evidence"],
]);

function copiedCellValue(value: unknown, cachedResult: unknown): unknown {
  if (
    value &&
    typeof value === "object" &&
    "formula" in value &&
    typeof (value as { formula?: unknown }).formula === "string"
  ) {
    return (
      cachedResult ??
      (value as { result?: unknown }).result ??
      "Model output unavailable"
    );
  }
  return value;
}

function appendEvidenceSheet(
  target: ExcelWorkbook["worksheets"][0] | undefined,
  source: ExcelWorkbook["worksheets"][0] | undefined,
  heading: string,
) {
  if (!target || !source) return null;
  const start = target.rowCount + 3;
  const title = target.getCell(start, 1);
  title.value = heading;
  title.font = {
    name: "Arial",
    size: 11,
    bold: true,
    color: { argb: "FFFFFF" },
  };
  title.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17365D" },
  };

  source.eachRow({ includeEmpty: false }, (sourceRow, sourceRowNumber) => {
    sourceRow.eachCell({ includeEmpty: false }, (sourceCell, column) => {
      const targetCell = target.getCell(start + sourceRowNumber, column);
      targetCell.value = copiedCellValue(
        sourceCell.value,
        sourceCell.result,
      ) as null | string | number | boolean | Date;
      targetCell.style = { ...sourceCell.style };
      if (sourceCell.numFmt) targetCell.numFmt = sourceCell.numFmt;
    });
  });
  return { rowOffset: start, startRow: start + 1 };
}

/** Plain text of a cell value: string, rich text, number or empty. Used by the
 *  register scan and its guard so both read a cell the same way (loop#036). */
function cellText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "richText" in value) {
    return (value as { richText: { text: string }[] }).richText
      .map((part) => part.text)
      .join("");
  }
  return String(value);
}

const SCOPE_CLASSIFICATIONS = new Set([
  "embedded",
  "separately_added",
  "excluded_tbc",
  "not_applicable",
]);

function buildCostBoardGrid(
  target: ExcelWorkbook["worksheets"][0] | undefined,
  builderSource: ExcelWorkbook["worksheets"][0] | undefined,
) {
  if (!target) return;
  const valueCell = (label: string, column = 3) =>
    target.getCell(
      requireRowByFirstCell(target, label, `Cost Board ${label}`),
      column,
    );
  const valueCellMatching = (
    matcher: string | ((label: string) => boolean),
    description: string,
    column = 3,
  ) =>
    target.getCell(
      requireRowByFirstCell(target, matcher, `Cost Board ${description}`),
      column,
    );
  const hasLoadedEvidence = findRowByFirstCell(target, "Loaded building works");
  const setMergedHeader = (
    row: number,
    start: number,
    end: number,
    label: string,
  ) => {
    if (start !== end) target.mergeCells(row, start, row, end);
    boardHeader(target.getCell(row, start), label);
  };
  const setBoardValue = (
    cell: ExcelJS.Cell,
    value: unknown,
    numFmt?: string,
  ) => {
    cell.value = value as ExcelJS.CellValue;
    cell.font = {
      name: "Arial",
      size: 9,
      bold: true,
      color: { argb: C.linkGreen },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "right",
      wrapText: true,
    };
    if (numFmt) cell.numFmt = numFmt;
  };
  const moneyLine = (
    row: number,
    label: string,
    sourceLabel: string,
    note: string,
  ) => {
    target.mergeCells(row, 10, row, 13);
    boardLabel(target.getCell(row, 10), label);
    target.mergeCells(row, 14, row, 15);
    const source = valueCell(sourceLabel);
    boardFormula(
      target.getCell(row, 14),
      `'${target.name}'!${source.address}/1000000`,
      (Number(boardCachedValue(source)) || 0) / 1_000_000,
      "0.0",
    );
    target.mergeCells(row, 16, row, 18);
    boardLabel(target.getCell(row, 16), note);
    target.getRow(row).height = 18;
  };

  boardTitle(target, 1, 10, 18, "COST & FUNDING — BOARD VIEW");
  target.mergeCells("J2:R2");
  boardLabel(
    target.getCell("J2"),
    hasLoadedEvidence
      ? "Adopted evidence, embedded versus separate allowances, funding treatment and counterparty diagnostics."
      : "Legacy rate-mode cost mechanics, funding treatment and counterparty diagnostics; no loaded source-cost basis is attached.",
  );

  boardSection(target, 4, 10, 18, "ADOPTED COST & ALLOWANCES");
  setMergedHeader(5, 10, 13, "Cost component");
  setMergedHeader(5, 14, 15, "Value ($m)");
  setMergedHeader(5, 16, 18, "Basis / treatment");
  if (hasLoadedEvidence) {
    moneyLine(
      6,
      "Loaded building works",
      "Loaded building works",
      "Embedded preliminaries, overhead and margin",
    );
    moneyLine(
      7,
      "Loaded site / outdoor works",
      "Loaded site / outdoor works",
      "First-class absolute site cost",
    );
    moneyLine(
      8,
      "Adopted construction",
      "Adopted construction",
      "Fixed planning base; provisional GFA allocation",
    );
    moneyLine(
      9,
      "Construction contingency",
      "Construction contingency",
      "Separately added allowance",
    );
    moneyLine(
      10,
      "Professional fees / consultants",
      "Consultants",
      "Separately added allowance",
    );
    moneyLine(
      11,
      "Consents / statutory costs",
      "Statutory costs",
      "Separate lump sum",
    );
    moneyLine(
      12,
      "Development management fee",
      "Development management fee",
      "Separately added allowance",
    );
  } else {
    moneyLine(
      6,
      "Gross pre-finance TDC",
      GROSS_TOTAL_DEVELOPMENT_COST_LABEL,
      "Existing legacy calculation; no loaded source basis",
    );
    moneyLine(7, "Preliminaries", "Preliminaries", "Existing rate-mode input");
    moneyLine(
      8,
      "Builder margin and overhead",
      "Builder margin and overhead",
      "Existing rate-mode input",
    );
    moneyLine(
      9,
      "Construction contingency",
      "Construction contingency",
      "Existing rate-mode input",
    );
    moneyLine(10, "Consultants", "Consultants", "Existing rate-mode input");
    moneyLine(
      11,
      "Statutory costs",
      "Statutory costs",
      "Existing rate-mode input",
    );
    moneyLine(
      12,
      "Development management fee",
      "Development management fee",
      "Existing rate-mode input",
    );
  }

  boardSection(target, 14, 10, 18, "CONSTRUCTION COST BRIDGE");
  setMergedHeader(15, 10, 13, "Bridge item");
  setMergedHeader(15, 14, 15, "Value ($m)");
  setMergedHeader(15, 16, 18, "Board interpretation");
  if (hasLoadedEvidence) {
    moneyLine(
      16,
      "Adopted construction cost",
      "Adopted construction cost",
      "Loaded source planning base",
    );
    moneyLine(
      17,
      "Building-package variance",
      "Building-package variance",
      "Source upper-bound bridge",
    );
    moneyLine(
      18,
      "Site / outdoor variance",
      "Site/outdoor variance",
      "Source upper-bound bridge",
    );
    moneyLine(
      19,
      "Quote-line rounding",
      "Quote-line rounding reconciliation",
      "Exact dollar reconciliation; not expressed in $m",
    );
    const quoteRounding = valueCell("Quote-line rounding reconciliation");
    boardFormula(
      target.getCell(19, 14),
      `'${target.name}'!${quoteRounding.address}`,
      Number(boardCachedValue(quoteRounding)) || 0,
      "$#,##0.00;($#,##0.00);-",
    );
    moneyLine(
      20,
      "Upper-bound comparator",
      "Upper-bound comparator",
      "Un-escalated comparator; not adopted base",
    );
  } else {
    target.mergeCells("J16:R20");
    boardLabel(
      target.getCell("J16"),
      "No loaded source-cost bridge is attached to this legacy scenario. Existing cost formulas and rate-mode assumptions remain authoritative; no upper-bound comparator is inferred.",
    );
    target.getCell("J16").font = {
      ...target.getCell("J16").font,
      italic: true,
    };
    target.getRow(16).height = 50;
  }

  boardSection(target, 22, 10, 18, "SCOPE / DOUBLE-COUNT REGISTER");
  setMergedHeader(23, 10, 11, "Scope");
  setMergedHeader(23, 12, 13, "Classification");
  setMergedHeader(23, 14, 15, "Model mapping");
  setMergedHeader(23, 16, 18, "Disclosure");
  // loop#036: the board block mirrors whatever scope register the sheet carries
  // (Kurraba: 8 rows, Versatile RevG: 13) instead of a hard-coded Kurraba label
  // list that threw on any other basis. The block keeps its legacy 8-row height
  // as a floor; a longer register pushes every later section down by `off`.
  const SCOPE_BLOCK_MIN_ROWS = 8;
  let scopeRows = SCOPE_BLOCK_MIN_ROWS;
  let off = 0;
  if (hasLoadedEvidence) {
    const scopeHeaderRow = requireRowByFirstCell(
      target,
      "Scope",
      "Cost Board scope header",
    );
    const scopeSourceRows: number[] = [];
    for (
      let candidate = scopeHeaderRow + 1;
      candidate <= target.rowCount;
      candidate += 1
    ) {
      if (
        !SCOPE_CLASSIFICATIONS.has(cellText(target.getCell(candidate, 2).value))
      )
        break;
      scopeSourceRows.push(candidate);
    }
    if (scopeSourceRows.length === 0) {
      throw new Error("Missing Cost Board scope-register rows");
    }
    // Fail loud, not short: a register row whose classification is not one of
    // the four values would otherwise end the scan and silently drop the rest.
    // The scan stopped at a row whose classification column is not one of the
    // four values. A section title is a merged cell, so columns 1 and 2 read the
    // same text; a register row with a mis-typed classification has a distinct
    // column-2 value and must not be dropped in silence.
    const afterScan = scopeSourceRows[scopeSourceRows.length - 1] + 1;
    const strayLabel = cellText(target.getCell(afterScan, 1).value);
    const strayClassification = cellText(target.getCell(afterScan, 2).value);
    if (
      strayClassification.trim() !== "" &&
      !target.getCell(afterScan, 2).isMerged &&
      strayClassification !== strayLabel
    ) {
      throw new Error(
        `Cost Board scope-register row ${afterScan} ("${strayLabel}") has an unknown classification "${strayClassification}"`,
      );
    }
    scopeRows = Math.max(SCOPE_BLOCK_MIN_ROWS, scopeSourceRows.length);
    off = scopeRows - SCOPE_BLOCK_MIN_ROWS;
    scopeSourceRows.forEach((sourceRow, index) => {
      const row = 24 + index;
      [
        [10, 11, 1],
        [12, 13, 2],
        [14, 15, 3],
        [16, 18, 4],
      ].forEach(([start, end, sourceColumn]) => {
        target.mergeCells(row, start, row, end);
        boardLink(
          target.getCell(row, start),
          target.getCell(sourceRow, sourceColumn),
        );
        target.getCell(row, start).font = {
          ...target.getCell(row, start).font,
          size: 8,
          bold: false,
        };
        target.getCell(row, start).alignment = {
          vertical: "middle",
          wrapText: true,
        };
      });
      target.getRow(row).height = 20;
    });
  } else {
    target.mergeCells("J24:R31");
    boardLabel(
      target.getCell("J24"),
      "No loaded scope register is attached. Legacy inclusions, exclusions and TBC items remain visible in the unchanged source register at left and must be reconciled before treating this scenario as evidence-backed.",
    );
    target.getCell("J24").font = {
      ...target.getCell("J24").font,
      italic: true,
    };
    target.getRow(24).height = 50;
  }

  boardSection(target, 33 + off, 10, 18, "SOURCES & USES");
  setMergedHeader(34 + off, 10, 13, "Funding item");
  setMergedHeader(34 + off, 14, 15, "Value ($m)");
  setMergedHeader(34 + off, 16, 18, "Treatment");
  moneyLine(
    35 + off,
    "Gross pre-finance TDC",
    "Gross TDC (uses)",
    "Unchanged by operator contribution",
  );
  moneyLine(
    36 + off,
    "Operator contribution",
    INSPIRED_REDDAM_CONTRIBUTION_LABEL,
    "Funding source; reduces actual debt",
  );
  moneyLine(
    37 + off,
    "Gross debt capacity",
    "Gross debt capacity",
    "67.5% capacity before contribution",
  );
  moneyLine(
    38 + off,
    "Actual construction debt",
    "Actual construction debt",
    "Gross capacity less contribution",
  );
  moneyLine(
    39 + off,
    "Developer equity",
    "Developer equity",
    "Not reduced by the contribution",
  );
  moneyLine(
    40 + off,
    TOTAL_FUNDING_SOURCES_LABEL,
    TOTAL_FUNDING_SOURCES_LABEL,
    "Must equal gross pre-finance TDC",
  );
  moneyLine(
    41 + off,
    ALL_IN_COST_SHORT_LABEL,
    ALL_IN_COST_LABEL,
    "Includes financing costs and fees",
  );

  boardSection(
    target,
    43 + off,
    10,
    18,
    "FIXED COST & CONTRIBUTION SENSITIVITIES",
  );
  [
    [10, 11, "Case"],
    [12, 12, "Construction"],
    [13, 13, "Timing"],
    [14, 14, RENT_PCT_REVENUE_SENSITIVITY_HEADER],
    [15, 15, OPERATOR_NPV_LABEL],
    [16, 16, "Ungeared"],
    [17, 17, "Geared"],
    [18, 18, YIELD_ON_COST_LABEL],
  ].forEach(([start, end, label]) =>
    setMergedHeader(44 + off, Number(start), Number(end), String(label)),
  );
  const sensitivityHeader = findRowByFirstCell(target, "Case");
  if (sensitivityHeader > 0) {
    for (let index = 0; index < 4; index += 1) {
      const row = 45 + off + index;
      const sourceRow = sensitivityHeader + 1 + index;
      target.mergeCells(row, 10, row, 11);
      const mappings = [
        [10, 1, undefined],
        [12, 2, NF.currency],
        [13, 3, undefined],
        [14, 5, NF.pct],
        [15, 6, NF.currency],
        [16, 7, NF.pct],
        [17, 8, NF.pct],
        [18, 9, NF.pct],
      ] as const;
      mappings.forEach(([targetColumn, sourceColumn, numFmt]) => {
        boardLink(
          target.getCell(row, targetColumn),
          target.getCell(sourceRow, sourceColumn),
          undefined,
          numFmt,
        );
        target.getCell(row, targetColumn).font = {
          ...target.getCell(row, targetColumn).font,
          size: 8,
          bold: false,
        };
      });
      target.getRow(row).height = 25;
    }
  } else {
    target.mergeCells(`J${45 + off}:R${48 + off}`);
    boardLabel(
      target.getCell(`J${45 + off}`),
      "No fixed loaded-cost contribution sensitivities are configured for this legacy scenario. Existing scenario calculations remain unchanged.",
    );
    target.getCell(`J${45 + off}`).font = {
      ...target.getCell(`J${45 + off}`).font,
      italic: true,
    };
    target.getRow(45 + off).height = 40;
  }

  boardSection(target, 50 + off, 10, 18, "DEVELOPER COUNTERPARTY DIAGNOSTICS");
  const diagnosticLabels = [
    ["Matched ungeared return", "Matched ungeared return", NF.pct],
    ["Matched geared reference", "Matched geared reference", NF.pct],
    [YIELD_ON_COST_LABEL, YIELD_ON_COST_LABEL, NF.pct],
  ] as const;
  diagnosticLabels.forEach(([label, sourcePrefix, numFmt], index) => {
    const start = 10 + index * 3;
    target.mergeCells(51 + off, start, 51 + off, start + 2);
    boardLabel(target.getCell(51 + off, start), label);
    target.mergeCells(52 + off, start, 52 + off, start + 2);
    boardLink(
      target.getCell(52 + off, start),
      valueCellMatching(
        (sourceLabel) =>
          sourcePrefix === YIELD_ON_COST_LABEL
            ? sourceLabel === sourcePrefix
            : sourceLabel.startsWith(sourcePrefix),
        label,
      ),
      undefined,
      numFmt,
    );
  });

  boardSection(target, 54 + off, 10, 18, "BUILDER QUOTE EVIDENCE");
  [
    [10, 11, "Builder"],
    [12, 12, "Bid date"],
    [13, 14, "Reported TPC"],
    [15, 16, "LFL TPC"],
    [17, 18, "Scope / benchmark"],
  ].forEach(([start, end, label]) =>
    setMergedHeader(55 + off, Number(start), Number(end), String(label)),
  );
  const builderHeader = findRowByFirstCell(builderSource, "Builder");
  if (
    builderSource &&
    builderHeader > 0 &&
    builderSource.getCell(builderHeader + 1, 1).value
  ) {
    const sourceRow = builderHeader + 1;
    target.mergeCells(56 + off, 10, 56 + off, 11);
    boardLabel(
      target.getCell(56 + off, 10),
      builderSource.getCell(sourceRow, 1).text,
    );
    boardLabel(
      target.getCell(56 + off, 12),
      builderSource.getCell(sourceRow, 2).text,
    );
    target.mergeCells(56 + off, 13, 56 + off, 14);
    setBoardValue(
      target.getCell(56 + off, 13),
      Number(boardCachedValue(builderSource.getCell(sourceRow, 3))),
      NF.currency,
    );
    target.mergeCells(56 + off, 15, 56 + off, 16);
    setBoardValue(
      target.getCell(56 + off, 15),
      Number(boardCachedValue(builderSource.getCell(sourceRow, 4))),
      NF.currency,
    );
    target.mergeCells(56 + off, 17, 56 + off, 18);
    boardLabel(
      target.getCell(56 + off, 17),
      builderSource.getCell(sourceRow, 10).text ||
        `Exclusions: ${builderSource.getCell(sourceRow, 7).text || "—"}; D7: ${builderSource.getCell(sourceRow, 8).text || "—"}; flag: ${builderSource.getCell(sourceRow, 9).text || "—"}`,
    );
    target.getRow(56 + off).height = 42;
  } else {
    target.mergeCells(56 + off, 10, 56 + off, 18);
    boardLabel(target.getCell(56 + off, 10), "No builder quotes loaded.");
    target.getRow(56 + off).height = 30;
  }
  target.mergeCells(`J${57 + off}:R${57 + off}`);
  boardLabel(
    target.getCell(`J${57 + off}`),
    "Project-specific quote comparison only; scope exclusions and market benchmarking remain subject to reconciliation.",
  );
  target.getCell(`J${57 + off}`).font = {
    ...target.getCell(`J${57 + off}`).font,
    italic: true,
  };

  [20, 12, 12, 12, 12, 12, 12, 12, 20].forEach((width, index) => {
    target.getColumn(10 + index).width = width;
  });
  [3, 13, 21].forEach((row) => {
    target.getRow(row).height = 5;
  });
  target.getRow(23 + scopeRows).addPageBreak();
  target.pageSetup = { ...target.pageSetup, printTitlesRow: undefined };
  setBoardPresentation(target, `J1:R${57 + off}`, "J1");
}

function addNativeTableFromRange(
  worksheet: ExcelWorkbook["worksheets"][0] | undefined,
  name: string,
  headerRow: number,
  lastRow: number,
  lastColumn: number,
) {
  if (!worksheet || lastRow <= headerRow || lastColumn < 1) return;
  const columns = Array.from({ length: lastColumn }, (_, index) => ({
    name: worksheet.getCell(headerRow, index + 1).text || `Column ${index + 1}`,
  }));
  const rows = Array.from({ length: lastRow - headerRow }, (_, rowIndex) =>
    Array.from(
      { length: lastColumn },
      (_, columnIndex) =>
        worksheet.getCell(headerRow + rowIndex + 1, columnIndex + 1).value,
    ),
  );
  worksheet.addTable({
    name,
    ref: `A${headerRow}`,
    headerRow: true,
    totalsRow: false,
    style: {
      theme: "TableStyleMedium2",
      showRowStripes: true,
      showFirstColumn: false,
      showLastColumn: false,
    },
    columns,
    rows,
  });
}

function findRowByFirstCell(
  worksheet: ExcelWorkbook["worksheets"][0] | undefined,
  value: string,
) {
  if (!worksheet) return 0;
  for (let row = 1; row <= worksheet.rowCount; row += 1) {
    if (worksheet.getCell(row, 1).value === value) return row;
  }
  return 0;
}

function requireRowByFirstCell(
  worksheet: ExcelWorkbook["worksheets"][0] | undefined,
  matcher: string | ((label: string) => boolean),
  description: string,
) {
  if (!worksheet) throw new Error(`Missing worksheet for ${description}`);
  for (let row = 1; row <= worksheet.rowCount; row += 1) {
    const label = String(worksheet.getCell(row, 1).value ?? "");
    if (
      (typeof matcher === "string" && label === matcher) ||
      (typeof matcher === "function" && matcher(label))
    ) {
      return row;
    }
  }
  throw new Error(`Missing ${description} row in ${worksheet.name}`);
}

function boardCachedValue(cell: ExcelJS.Cell): unknown {
  if (cell.result !== undefined) return cell.result;
  const value = cell.value;
  if (value && typeof value === "object" && "result" in value) {
    return value.result;
  }
  return value;
}

function boardFormula(
  target: ExcelJS.Cell,
  formula: string,
  result: unknown,
  numFmt?: string,
) {
  target.value = {
    formula: normalizeFormulaExpression(formula),
    result,
  } as ExcelJS.CellValue;
  target.font = {
    name: "Arial",
    size: 10,
    bold: true,
    color: { argb: C.formulaBlack },
  };
  target.alignment = {
    vertical: "middle",
    horizontal: "right",
    wrapText: true,
  };
  if (numFmt) target.numFmt = numFmt;
}

function boardLink(
  target: ExcelJS.Cell,
  source: ExcelJS.Cell,
  formula = `'${source.worksheet.name}'!${source.address}`,
  numFmt = source.numFmt,
) {
  boardFormula(target, formula, boardCachedValue(source), numFmt);
}

function boardTitle(
  sheet: ExcelJS.Worksheet,
  row: number,
  startColumn: number,
  endColumn: number,
  title: string,
) {
  sheet.mergeCells(row, startColumn, row, endColumn);
  const cell = sheet.getCell(row, startColumn);
  cell.value = title;
  cell.font = {
    name: "Arial",
    size: 15,
    bold: true,
    color: { argb: C.headerText },
  };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  cell.alignment = { vertical: "middle", horizontal: "left" };
  sheet.getRow(row).height = 25;
}

function boardSection(
  sheet: ExcelJS.Worksheet,
  row: number,
  startColumn: number,
  endColumn: number,
  title: string,
) {
  sheet.mergeCells(row, startColumn, row, endColumn);
  const cell = sheet.getCell(row, startColumn);
  cell.value = title;
  cell.font = {
    name: "Arial",
    size: 10,
    bold: true,
    color: { argb: C.headerText },
  };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "385D7A" },
  };
  cell.alignment = { vertical: "middle", horizontal: "left" };
  sheet.getRow(row).height = 20;
}

function boardHeader(cell: ExcelJS.Cell, value: string) {
  cell.value = value;
  cell.font = {
    name: "Arial",
    size: 8.5,
    bold: true,
    color: { argb: C.headerText },
  };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "5B7790" },
  };
  cell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
}

function boardLabel(cell: ExcelJS.Cell, value: string) {
  cell.value = value;
  cell.font = {
    name: "Arial",
    size: 8.5,
    color: { argb: "334155" },
  };
  cell.alignment = { vertical: "middle", wrapText: true };
}

function boardMetric(
  sheet: ExcelJS.Worksheet,
  labelRow: number,
  startColumn: number,
  endColumn: number,
  label: string,
  source: ExcelJS.Cell,
  numFmt = source.numFmt,
  formula?: string,
  result?: unknown,
) {
  sheet.mergeCells(labelRow, startColumn, labelRow, endColumn);
  boardLabel(sheet.getCell(labelRow, startColumn), label);
  sheet.mergeCells(labelRow + 1, startColumn, labelRow + 1, endColumn);
  const valueCell = sheet.getCell(labelRow + 1, startColumn);
  if (formula) {
    boardFormula(valueCell, formula, result, numFmt);
  } else {
    boardLink(valueCell, source, undefined, numFmt);
  }
  valueCell.font = { ...valueCell.font, size: 12, bold: true };
  valueCell.alignment = {
    ...valueCell.alignment,
    horizontal: "left",
  };
}

function setBoardPresentation(
  sheet: ExcelJS.Worksheet,
  printArea: string,
  topLeftCell: string,
) {
  // ExcelJS adds absolute column markers while serializing defined names.
  // Passing an already column-absolute address produces invalid `$$D` refs.
  sheet.pageSetup = {
    ...sheet.pageSetup,
    printArea: rowAbsolutePrintArea(printArea),
  };
  sheet.views = [
    {
      state: "frozen",
      xSplit: Number(sheet.getCell(topLeftCell).col) - 1,
      topLeftCell,
      showGridLines: false,
    },
  ];
}

function buildOperatorBoardGrid(
  target: ExcelJS.Worksheet | undefined,
  opAnnual: ExcelJS.Worksheet | undefined,
  assumptions: ExcelJS.Worksheet | undefined,
) {
  if (!target || !opAnnual || !assumptions) return;
  const start = 4;
  const end = 13;
  boardTitle(target, 1, start, end, "OPERATOR CASE — BOARD VIEW");
  target.mergeCells(2, start, 2, end);
  boardLabel(
    target.getCell(2, start),
    "Operator affordability and cash generation lead; developer returns are shown only as counterparty diagnostics.",
  );
  boardSection(target, 4, start, end, "OPERATOR ECONOMICS");

  const row = (label: string) =>
    requireRowByFirstCell(target, label, `Operator Case ${label}`);
  const opRow = (label: string) =>
    requireRowByFirstCell(opAnnual, label, `operator annual ${label}`);
  const source = (label: string) => target.getCell(row(label), 2);
  const opSource = (label: string, column = 2) =>
    opAnnual.getCell(opRow(label), column);

  boardMetric(
    target,
    5,
    4,
    6,
    OPERATOR_NPV_LABEL,
    source(OPERATOR_NPV_LABEL),
    NF.currency,
  );
  boardMetric(
    target,
    5,
    7,
    9,
    RENT_PCT_REVENUE_Y1_LABEL,
    source(RENT_PCT_REVENUE_Y1_LABEL),
    NF.pct,
  );
  boardMetric(
    target,
    5,
    10,
    13,
    Y1_EBITDA_LABEL,
    source(Y1_EBITDA_LABEL),
    NF.currency,
  );
  boardMetric(
    target,
    7,
    4,
    6,
    OPERATOR_PAYBACK_LABEL,
    source(OPERATOR_PAYBACK_LABEL),
    NF.num,
  );
  boardMetric(
    target,
    7,
    7,
    9,
    "Scheduled classroom seats",
    opSource("Scheduled classroom seats"),
    NF.num,
  );
  const scheduled =
    Number(boardCachedValue(opSource("Scheduled classroom seats"))) || 0;
  const planning = Number(boardCachedValue(assumptions.getCell("B123"))) || 0;
  boardMetric(
    target,
    7,
    10,
    13,
    "Physical delivery bridge",
    assumptions.getCell("B123"),
    NF.num,
    `'Assumptions'!$B$123-'Op P&L'!B${opRow("Scheduled classroom seats")}`,
    planning - scheduled,
  );

  boardSection(target, 10, start, end, "OPERATOR CASHFLOW — MILESTONE YEARS");
  boardHeader(target.getCell(11, start), "Metric");
  const years = [1, 2, 3, 4, 5, 10, 15, 20, 25];
  years.forEach((year, index) =>
    boardHeader(target.getCell(11, 5 + index), `Y${year}`),
  );
  const scheduleRows = [
    { label: "Enrolment", source: "Enrolment", numFmt: NF.num },
    { label: "Revenue", source: "TOTAL REVENUE", numFmt: NF.currencyM },
    {
      label: "Total occupancy cost",
      source: "Rent (gross)",
      secondSource: "Amenity LL rent (sports + parking + covered)",
      numFmt: NF.currencyM,
    },
    { label: "Rent / revenue", source: "Rent % revenue", numFmt: NF.pct },
    { label: "EBITDA", source: "EBITDA", numFmt: NF.currencyM },
    {
      label: "Cumulative cash",
      source: "Cumulative cashflow",
      numFmt: NF.currencyM,
    },
  ];
  scheduleRows.forEach((definition, rowIndex) => {
    const targetRow = 12 + rowIndex;
    boardLabel(target.getCell(targetRow, start), definition.label);
    years.forEach((year, columnIndex) => {
      const sourceColumn = year + 1;
      const primary = opSource(definition.source, sourceColumn);
      const formula = definition.secondSource
        ? `'Op P&L'!${primary.address}+'Op P&L'!${opSource(definition.secondSource, sourceColumn).address}`
        : `'Op P&L'!${primary.address}`;
      const result =
        (Number(boardCachedValue(primary)) || 0) +
        (definition.secondSource
          ? Number(
              boardCachedValue(opSource(definition.secondSource, sourceColumn)),
            ) || 0
          : 0);
      boardFormula(
        target.getCell(targetRow, 5 + columnIndex),
        formula,
        result,
        definition.numFmt,
      );
      target.getCell(targetRow, 5 + columnIndex).font = {
        ...target.getCell(targetRow, 5 + columnIndex).font,
        size: 8.5,
      };
    });
  });

  boardSection(target, 19, start, end, "DEVELOPER COUNTERPARTY DIAGNOSTICS");
  const primaryIrrRow = requireRowByFirstCell(
    target,
    (label) => label.startsWith("Primary IRR (ungeared,"),
    "primary ungeared IRR",
  );
  const matchedGearedRow = requireRowByFirstCell(
    target,
    (label) =>
      // "project " prefix added to the swept exit labels (loop#001); accept both spellings so
      // label consumers can never silently rebind to the whole-of-life row again.
      /^IRR \(geared, (?:project )?Y\d+ early exit\)$/.test(label) ||
      label === "IRR (geared, sale at completion)",
    "matched geared achieved IRR",
  );
  boardMetric(
    target,
    20,
    4,
    6,
    "Primary ungeared IRR",
    target.getCell(primaryIrrRow, 2),
    NF.pct,
  );
  boardMetric(
    target,
    20,
    7,
    8,
    "Matched geared achieved IRR",
    target.getCell(matchedGearedRow, 2),
    NF.pct,
  );
  boardMetric(
    target,
    20,
    9,
    11,
    YIELD_ON_COST_LABEL,
    source(YIELD_ON_COST_LABEL),
    NF.pct,
  );
  boardMetric(
    target,
    20,
    12,
    13,
    "Equity multiple",
    source("Money multiple"),
    NF.multiple,
  );

  target.getColumn(4).width = 20;
  for (let column = 5; column <= 13; column += 1) {
    target.getColumn(column).width = 11.5;
  }
  setBoardPresentation(target, "D1:M37", "D1");
}

function buildRentBoardGrid(target: ExcelJS.Worksheet | undefined) {
  if (!target) return;
  boardTitle(target, 1, 5, 13, "RENT & NEGOTIATION — BOARD VIEW");
  target.mergeCells("E2:M2");
  boardLabel(
    target.getCell("E2"),
    "Separate the landlord ask, developer financing diagnostic and operator affordability decision.",
  );
  const groups = [
    { start: 5, title: "LANDLORD ASK" },
    { start: 8, title: "DEVELOPER FINANCING DIAGNOSTIC" },
    { start: 11, title: "OPERATOR AFFORDABILITY / BOARD POSITION" },
  ];
  groups.forEach(({ start, title }) =>
    boardSection(target, 4, start, start + 2, title),
  );

  const write = (
    row: number,
    startColumn: number,
    label: string,
    sourceMatcher: string | ((label: string) => boolean),
    numFmt?: string,
  ) => {
    target.mergeCells(row, startColumn, row, startColumn + 1);
    boardLabel(target.getCell(row, startColumn), label);
    const sourceRow = requireRowByFirstCell(
      target,
      sourceMatcher,
      `Rent Board ${label}`,
    );
    boardLink(
      target.getCell(row, startColumn + 2),
      target.getCell(sourceRow, 2),
      undefined,
      numFmt,
    );
    target.getCell(row, startColumn + 2).font = {
      ...target.getCell(row, startColumn + 2).font,
      size: 9,
    };
  };
  write(
    5,
    5,
    "Building rent ask (Y1)",
    "Developer ask — scenario rent (Y1, gross)",
    NF.currencyM,
  );
  write(
    6,
    5,
    "Amenity rent (Y1)",
    "Amenity rent — sports, parking & covered play area (Y1)",
    NF.currencyM,
  );
  write(7, 5, "Total occupancy ask", "Total occupancy ask (Y1)", NF.currencyM);
  write(
    8,
    5,
    "Effective building rent",
    "Effective building rent Y1 (face − amortised incentive)",
    NF.currencyM,
  );

  write(
    5,
    8,
    "Stated ungeared hurdle",
    "Stated ungeared project hurdle",
    NF.pct,
  );
  write(
    6,
    8,
    "Achieved geared IRR",
    "Achieved matched-horizon geared IRR at current ask",
    NF.pct,
  );
  write(
    7,
    8,
    "Financing-equivalent rent",
    (label) =>
      /^Financing-equivalent building rent \(.*model output\)$/.test(label),
    NF.currencyM,
  );
  write(
    8,
    8,
    "Ask less financing reference",
    "Developer margin / overreach vs financing reference",
    NF.currencyM,
  );

  write(5, 11, "Current rent / revenue", "Current Y1 rent burden", NF.pct);
  write(
    6,
    11,
    "Screening threshold",
    "Operator affordability screening threshold",
    NF.pct,
  );
  write(7, 11, "Assessment status", "Operator assessment status");
  write(8, 11, "Decision evidence", "Operator assessment evidence");
  target.getRow(8).height = 72;

  boardSection(target, 10, 5, 13, "NOTES");
  const notes = [
    "1. Financing-equivalent rent is a developer funding diagnostic; it does not establish rent entitlement.",
    "2. Operator affordability uses total occupancy cost, including separately modelled amenity rent.",
    "3. Any Year 1 affordability excess is a Board funding and phasing decision, not an automated recommendation.",
  ];
  notes.forEach((note, index) => {
    target.mergeCells(11 + index, 5, 11 + index, 13);
    boardLabel(target.getCell(11 + index, 5), note);
    target.getRow(11 + index).height = 18;
  });
  for (let column = 5; column <= 13; column += 1)
    target.getColumn(column).width = 14;
  target.getColumn(13).width = 24;
  setBoardPresentation(target, "E1:M13", "E1");
}

function buildAssumptionsBoardGrid(
  target: ExcelJS.Worksheet | undefined,
  opAnnual: ExcelJS.Worksheet | undefined,
) {
  if (!target || !opAnnual) return;
  boardTitle(target, 1, 4, 11, "KEY ASSUMPTIONS — BOARD SUMMARY");
  target.mergeCells("D2:K2");
  boardLabel(
    target.getCell("D2"),
    "Decision-driving inputs only. The complete editable 142-row register remains in columns A:B.",
  );
  [
    "Assumption",
    "Value",
    "Unit",
    "Evidence date / source",
    "Confidence",
  ].forEach((label, index) => {
    const columns = [4, 5, 6, 7, 11];
    boardHeader(target.getCell(3, columns[index]), label);
  });
  target.mergeCells("G3:J3");

  const source = (row: number) => target.getCell(row, 2);
  const write = (
    row: number,
    label: string,
    sourceRow: number,
    unit: string,
    numFmt: string | undefined,
    evidence = "Scenario input",
    confidence = "Input",
    formula?: string,
    result?: unknown,
  ) => {
    boardLabel(target.getCell(row, 4), label);
    if (formula) boardFormula(target.getCell(row, 5), formula, result, numFmt);
    else
      boardLink(target.getCell(row, 5), source(sourceRow), undefined, numFmt);
    target.getCell(row, 5).font = { ...target.getCell(row, 5).font, size: 9 };
    boardLabel(target.getCell(row, 6), unit);
    target.mergeCells(row, 7, row, 10);
    boardLabel(target.getCell(row, 7), evidence);
    boardLabel(target.getCell(row, 11), confidence);
  };

  boardSection(target, 4, 4, 11, "CAPACITY & DEMAND");
  write(5, "Planning enrolment target", 123, "students", NF.num);
  const scheduledRow = requireRowByFirstCell(
    opAnnual,
    "Scheduled classroom seats",
    "scheduled classroom seats",
  );
  const scheduledCell = opAnnual.getCell(scheduledRow, 2);
  boardLabel(target.getCell(6, 4), "Scheduled classroom seats");
  boardLink(target.getCell(6, 5), scheduledCell, undefined, NF.num);
  boardLabel(target.getCell(6, 6), "seats");
  target.mergeCells("G6:J6");
  boardLabel(target.getCell("G6"), "Building schedule");
  boardLabel(target.getCell("K6"), "Calculated");
  write(
    7,
    "Physical delivery bridge",
    123,
    "seats",
    NF.num,
    "Target less scheduled seats",
    "Calculated",
    `'Assumptions'!$B$123-'Op P&L'!${scheduledCell.address}`,
    (Number(boardCachedValue(source(123))) || 0) -
      (Number(boardCachedValue(scheduledCell)) || 0),
  );
  write(8, "Opening utilisation", 52, "of target", NF.pct);
  write(9, "Ramp period", 51, "years", NF.num);

  boardSection(target, 10, 4, 11, "RENT & OPERATOR ECONOMICS");
  const blendedFeeCell = opAnnual.getCell(1, 2);
  write(
    11,
    "Blended fee (Y1)",
    38,
    "$ / student",
    NF.currency,
    "Calculated stage-weighted fee",
    "Calculated",
    `'${opAnnual.name}'!${blendedFeeCell.address}`,
    boardCachedValue(blendedFeeCell),
  );
  write(12, "Rent escalation", 111, "p.a.", NF.pct);
  write(13, "Operator discount rate", 14, "discount rate", NF.pct);

  boardSection(target, 14, 4, 11, "COST & ALLOWANCES");
  const evidenceDate = String(boardCachedValue(source(130)));
  const evidenceOwner = String(boardCachedValue(source(129))).split(" — ")[0];
  const evidence =
    evidenceDate === "Not loaded" || evidenceOwner === "Not loaded"
      ? "Not loaded"
      : `${evidenceDate} — ${evidenceOwner} development cost schedule`;
  write(
    15,
    "Adopted construction cost",
    127,
    "$m",
    "0.0",
    evidence,
    "Evidence-backed; GST unverified",
    "(B127+B128)/1000000",
    ((Number(boardCachedValue(source(127))) || 0) +
      (Number(boardCachedValue(source(128))) || 0)) /
      1_000_000,
  );
  write(
    16,
    "Contingency",
    133,
    "$m",
    "0.0",
    evidence,
    "Source amount",
    "B133/1000000",
    (Number(boardCachedValue(source(133))) || 0) / 1_000_000,
  );
  write(
    17,
    "Professional fees",
    136,
    "$m",
    "0.0",
    evidence,
    "Source amount",
    "B136/1000000",
    (Number(boardCachedValue(source(136))) || 0) / 1_000_000,
  );
  write(
    18,
    "Development fee",
    139,
    "$m",
    "0.0",
    evidence,
    "Source amount",
    "B139/1000000",
    (Number(boardCachedValue(source(139))) || 0) / 1_000_000,
  );
  write(
    19,
    "Consents",
    141,
    "$m",
    "0.0",
    evidence,
    "Source amount",
    "B141/1000000",
    (Number(boardCachedValue(source(141))) || 0) / 1_000_000,
  );

  boardSection(target, 20, 4, 11, "FUNDING & RETURNS");
  write(
    21,
    "Operator contribution",
    64,
    "$m",
    "0.0",
    "Scenario input",
    "Input",
    "B64/1000000",
    (Number(boardCachedValue(source(64))) || 0) / 1_000_000,
  );
  write(22, "Contribution timing", 142, "timing", undefined);
  write(23, "Construction LVR", 88, "of eligible cost", NF.pct);
  write(24, "Developer hurdle IRR", 15, "IRR", NF.pct);

  target.getColumn(4).width = 22;
  target.getColumn(5).width = 14;
  target.getColumn(6).width = 10;
  for (let column = 7; column <= 10; column += 1)
    target.getColumn(column).width = 10;
  target.getColumn(11).width = 16;
  setBoardPresentation(target, "D1:K24", "D1");
}

function buildDownsideBoardGrid(
  target: ExcelJS.Worksheet | undefined,
  headline: ExcelJS.Worksheet | undefined,
) {
  if (!target || !headline) return;
  boardTitle(target, 1, 7, 14, "DOWNSIDE — BOARD VIEW");
  boardSection(target, 3, 7, 14, "OPERATOR DOWNSIDE — AFFORDABILITY");
  const headlineSource = (label: string) =>
    headline.getCell(
      requireRowByFirstCell(headline, label, `Downside ${label}`),
      2,
    );
  boardMetric(
    target,
    4,
    7,
    8,
    Y1_EBITDA_LABEL,
    headlineSource(Y1_EBITDA_LABEL),
    NF.currencyM,
  );
  boardMetric(
    target,
    4,
    9,
    10,
    RENT_PCT_REVENUE_Y1_LABEL,
    headlineSource(RENT_PCT_REVENUE_Y1_LABEL),
    NF.pct,
  );
  boardMetric(
    target,
    4,
    11,
    12,
    "Peak cash trough",
    headlineSource("Peak cash trough"),
    NF.currencyM,
  );
  boardMetric(
    target,
    4,
    13,
    14,
    RAMP_RESERVES_REQUIRED_LABEL,
    headlineSource(RAMP_RESERVES_REQUIRED_LABEL),
    NF.currencyM,
  );
  boardSection(target, 7, 7, 14, "DEVELOPER COUNTERPARTY SENSITIVITIES");

  boardSection(target, 9, 7, 8, "CAP RATE");
  boardHeader(target.getCell(10, 7), "Cap-rate case");
  boardHeader(target.getCell(10, 8), "Developer ungeared IRR");
  target.getCell(10, 8).alignment = {
    ...target.getCell(10, 8).alignment,
    wrapText: true,
  };
  for (let index = 0; index < 5; index += 1) {
    boardLink(target.getCell(11 + index, 7), target.getCell(4 + index, 1));
    boardLink(
      target.getCell(11 + index, 8),
      target.getCell(4 + index, 2),
      undefined,
      NF.pct,
    );
    target.getCell(11 + index, 7).font = {
      ...target.getCell(11 + index, 7).font,
      size: 8.5,
    };
    target.getCell(11 + index, 8).font = {
      ...target.getCell(11 + index, 8).font,
      size: 8.5,
    };
  }

  const interestRow = requireRowByFirstCell(
    target,
    "INTEREST RATE SENSITIVITY",
    "interest sensitivity",
  );
  boardSection(target, 9, 10, 13, "INTEREST RATE");
  for (let column = 0; column < 4; column += 1) {
    boardHeader(
      target.getCell(10, 10 + column),
      String(
        boardCachedValue(target.getCell(interestRow + 3, 1 + column)) ?? "",
      ),
    );
  }
  for (let rowIndex = 0; rowIndex < 5; rowIndex += 1) {
    for (let column = 0; column < 4; column += 1) {
      const sourceCell = target.getCell(interestRow + 4 + rowIndex, 1 + column);
      boardLink(target.getCell(11 + rowIndex, 10 + column), sourceCell);
      target.getCell(11 + rowIndex, 10 + column).font = {
        ...target.getCell(11 + rowIndex, 10 + column).font,
        size: 8.5,
      };
    }
  }

  const lvrRow = requireRowByFirstCell(
    target,
    "CONSTRUCTION LVR SENSITIVITY",
    "construction LVR sensitivity",
  );
  boardSection(target, 17, 7, 11, "CONSTRUCTION LVR");
  target.mergeCells("G18:K18");
  boardLink(target.getCell("G18"), target.getCell(lvrRow + 1, 1));
  target.getCell("G18").alignment = { horizontal: "left", wrapText: true };
  for (let column = 0; column < 5; column += 1) {
    boardHeader(
      target.getCell(19, 7 + column),
      String(boardCachedValue(target.getCell(lvrRow + 3, 1 + column)) ?? ""),
    );
  }
  for (let rowIndex = 0; rowIndex < 5; rowIndex += 1) {
    for (let column = 0; column < 5; column += 1) {
      const sourceCell = target.getCell(lvrRow + 4 + rowIndex, 1 + column);
      boardLink(target.getCell(20 + rowIndex, 7 + column), sourceCell);
      target.getCell(20 + rowIndex, 7 + column).font = {
        ...target.getCell(20 + rowIndex, 7 + column).font,
        size: 8.5,
      };
    }
  }
  boardSection(target, 17, 13, 14, "BOARD READ");
  target.mergeCells("M18:N20");
  boardLabel(
    target.getCell("M18"),
    "Developer sensitivities test counterparty resilience. They do not override the operator affordability decision.",
  );
  target.mergeCells("M22:N24");
  boardLabel(
    target.getCell("M22"),
    "Read the operator strip first, then assess whether downside changes the commercial negotiation range.",
  );

  for (let column = 7; column <= 14; column += 1)
    target.getColumn(column).width = 16;
  target.getColumn(8).width = 22;
  setBoardPresentation(target, "G1:N24", "G1");
}

function rewriteMovedSheetReferences(
  wb: ExcelWorkbook,
  sourceSheet: string,
  targetSheet: string,
  rowOffset: number,
) {
  const escapedSource = sourceSheet.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const referencePattern = new RegExp(
    `'${escapedSource}'!(\\$?)([A-Z]{1,3})(\\$?)(\\d+)(?::(\\$?)([A-Z]{1,3})(\\$?)(\\d+))?`,
    "g",
  );
  const rewrite = (formula: string) =>
    formula.replace(
      referencePattern,
      (
        _match,
        absoluteColumn,
        column,
        absoluteRow,
        row,
        endAbsoluteColumn,
        endColumn,
        endAbsoluteRow,
        endRow,
      ) => {
        const start = `${absoluteColumn}${column}${absoluteRow}${Number(row) + rowOffset}`;
        const end = endColumn
          ? `:${endAbsoluteColumn}${endColumn}${endAbsoluteRow}${Number(endRow) + rowOffset}`
          : "";
        return `'${targetSheet}'!${start}${end}`;
      },
    );

  wb.eachSheet((worksheet) => {
    worksheet.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        const value = cell.value;
        if (
          !value ||
          typeof value !== "object" ||
          !("formula" in value) ||
          typeof value.formula !== "string"
        ) {
          return;
        }
        cell.value = { ...value, formula: rewrite(value.formula) };
      });
    });
  });

  const definedNames = wb.definedNames as unknown as {
    model: Array<{ name: string; ranges: string[] }>;
  };
  definedNames.model = definedNames.model.map((entry) => ({
    ...entry,
    ranges: entry.ranges.map(rewrite),
  }));
}

function appendPrimaryCharts(
  target: ExcelWorkbook["worksheets"][0] | undefined,
  source: ExcelWorkbook["worksheets"][0] | undefined,
) {
  if (!target || !source || typeof (source as any).getImages !== "function")
    return;
  const images = (source as any).getImages().slice(0, 2);
  if (images.length === 0) return;
  target.mergeCells("D23:M23");
  target.getCell("D23").value = "OPERATOR CASHFLOW CHARTS";
  target.getCell("D23").font = {
    name: "Arial",
    size: 10,
    bold: true,
    color: { argb: C.headerText },
  };
  target.getCell("D23").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "385D7A" },
  };
  const labels = [
    "Cashflow — EBITDA bars + cumulative line",
    "P&L bridge — Revenue, Rent, EBITDA",
  ];
  images.forEach((image: any, index: number) => {
    const startColumn = index === 0 ? 4 : 9;
    const endColumn = index === 0 ? 8 : 13;
    target.mergeCells(24, startColumn, 24, endColumn);
    target.getCell(24, startColumn).value = labels[index];
    target.getCell(24, startColumn).font = {
      name: "Arial",
      size: 8.5,
      bold: true,
      color: { argb: "1F1F1F" },
    };
    (target as any).addImage(image.imageId, {
      tl: { col: startColumn - 1, row: 24 },
      ext: { width: 400, height: 185 },
      editAs: "oneCell",
    });
  });
}

function replaceNonResponsiveTwoWaySensitivity(wb: ExcelWorkbook) {
  const source = wb.getWorksheet("Two-Way Sensitivity");
  if (!source) return;
  let baseColumn = 0;
  for (let column = 2; column <= source.columnCount; column += 1) {
    if (source.getCell(4, column).value === "Base") {
      baseColumn = column;
      break;
    }
  }
  if (baseColumn === 0) return;
  const rows: Array<[unknown, unknown]> = [];
  for (let row = 5; row <= source.rowCount; row += 1) {
    const label = source.getCell(row, 1).value;
    if (typeof label !== "string" || !label.startsWith("Cap rate")) continue;
    const valueCell = source.getCell(row, baseColumn);
    rows.push([label, copiedCellValue(valueCell.value, valueCell.result)]);
  }
  if (rows.length === 0) return;

  wb.removeWorksheet(source.id);
  const downside = wb.addWorksheet("Two-Way Sensitivity");
  downside.views = [{ state: "frozen", ySplit: 3 }];
  downside.getCell("A1").value = "CAP RATE DOWNSIDE";
  downside.getCell("A1").font = {
    name: "Arial",
    size: 14,
    bold: true,
    color: { argb: "17365D" },
  };
  downside.mergeCells("A1:B1");
  downside.getCell("A2").value =
    "One-way matched-horizon ungeared IRR stress. The loaded aggregate cost basis makes developer capex per sqm non-responsive, so the inactive second axis is omitted.";
  downside.getCell("A2").font = {
    name: "Arial",
    size: 9,
    italic: true,
    color: { argb: "5B6779" },
  };
  downside.getCell("A2").alignment = { wrapText: true };
  downside.mergeCells("A2:B2");
  downside.getRow(2).height = 30;
  ["Cap-rate case", "Developer ungeared IRR"].forEach((label, index) => {
    const cell = downside.getCell(3, index + 1);
    cell.value = label;
    cell.font = {
      name: "Arial",
      size: 10,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "17365D" },
    };
  });
  rows.forEach(([label, value], index) => {
    downside.getCell(index + 4, 1).value = label as string;
    downside.getCell(index + 4, 2).value = value as string | number | null;
    downside.getCell(index + 4, 2).note =
      "Model-recomputed sensitivity output for the stated cap-rate case; not a user override or editable assumption.";
    downside.getCell(index + 4, 2).numFmt = "0.0%";
  });
  downside.columns = [{ width: 26 }, { width: 24 }];
}

function appendCompactSensitivity(
  target: ExcelWorkbook["worksheets"][0] | undefined,
  source: ExcelWorkbook["worksheets"][0] | undefined,
  heading: string,
  columns: Array<{ sourceColumn: number; label: string }>,
) {
  if (!target || !source) return;
  const start = target.rowCount + 3;
  target.getCell(start, 1).value = heading;
  target.getCell(start, 1).font = {
    name: "Arial",
    size: 11,
    bold: true,
    color: { argb: "17365D" },
  };
  target.mergeCells(start, 1, start, columns.length);

  const sourceNote = copiedCellValue(
    source.getCell(2, 1).value,
    source.getCell(2, 1).result,
  );
  target.getCell(start + 1, 1).value =
    typeof sourceNote === "string" ? sourceNote : "";
  target.getCell(start + 1, 1).font = {
    name: "Arial",
    size: 9,
    italic: true,
    color: { argb: "5B6779" },
  };
  target.getCell(start + 1, 1).alignment = { wrapText: true };
  target.mergeCells(start + 1, 1, start + 1, columns.length);
  target.getRow(start + 1).height = 24;

  columns.forEach(({ label }, index) => {
    const cell = target.getCell(start + 3, index + 1);
    cell.value = label;
    cell.font = {
      name: "Arial",
      size: 10,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "17365D" },
    };
  });

  let targetRow = start + 4;
  const targetBaseRow = targetRow;
  for (let sourceRow = 5; sourceRow <= source.rowCount; sourceRow += 1) {
    if (!source.getCell(sourceRow, 1).value) continue;
    columns.forEach(({ sourceColumn, label }, index) => {
      const sourceCell = source.getCell(sourceRow, sourceColumn);
      const targetCell = target.getCell(targetRow, index + 1);
      const isBaseDelta =
        label === "Profit delta vs base" &&
        source.getCell(sourceRow, 1).value === "Base case";
      const sourceFormula = sourceCell.formula;
      let projectedFormula: string | undefined;
      if (
        heading === "INTEREST RATE SENSITIVITY" &&
        typeof sourceFormula === "string"
      ) {
        if (sourceColumn === 2) {
          projectedFormula = sourceFormula;
        } else if (sourceColumn === 4) {
          projectedFormula = sourceFormula.replace(
            new RegExp(`\\$?B\\$?${sourceRow}\\b`, "g"),
            `B${targetRow}`,
          );
        } else if (sourceColumn === 6) {
          projectedFormula = sourceFormula
            .replace(/\$D\$5\b/g, `$C$${targetBaseRow}`)
            .replace(
              new RegExp(`\\$?D\\$?${sourceRow}\\b`, "g"),
              `C${targetRow}`,
            );
        }
      }
      const copied = isBaseDelta
        ? 0
        : (copiedCellValue(sourceCell.value, sourceCell.result) as
            string | number | null);
      targetCell.value = projectedFormula
        ? { formula: projectedFormula, result: copied ?? undefined }
        : isBaseDelta
          ? 0
          : copied;
      if (sourceCell.numFmt) targetCell.numFmt = sourceCell.numFmt;
      targetCell.font = { name: "Arial", size: 9 };
    });
    targetRow += 1;
  }
}

function appendBoardActionRegister(
  target: ExcelWorkbook["worksheets"][0] | undefined,
  source: ExcelWorkbook["worksheets"][0] | undefined,
) {
  if (!target || !source) return null;
  const riskHeader = findRowByFirstCell(source, "Risk ID");
  const promptHeading = findRowByFirstCell(
    source,
    "MODEL-GENERATED DILIGENCE PROMPTS — NOT SITE FINDINGS",
  );
  const promptHeader = findRowByFirstCell(source, "Prompt ID");
  if (riskHeader === 0 || promptHeader === 0) return null;

  const start = target.rowCount + 3;
  // LibreOffice interprets ExcelJS's row-break id one row later than Excel.
  // Break on the preceding spacer so the heading starts the second print page
  // in both engines.
  target.getRow(start - 2).addPageBreak();
  target.getRow(start - 1).height = 24;
  target.getCell(start, 1).value = "PROPOSED ACTION REGISTER";
  target.getCell(start, 1).font = {
    name: "Arial",
    size: 11,
    bold: true,
    color: { argb: "17365D" },
  };
  target.mergeCells(start, 1, start, 5);

  const actionHeaders = [
    "ID",
    "Risk",
    "Owner",
    "Decision gate",
    "Residual / required action / status",
  ];
  const actionHeaderRow = start + 2;
  actionHeaders.forEach((label, index) => {
    const cell = target.getCell(actionHeaderRow, index + 1);
    cell.value = label;
    cell.font = {
      name: "Arial",
      size: 9,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "17365D" },
    };
    cell.alignment = { wrapText: true, vertical: "middle" };
  });

  let targetRow = actionHeaderRow + 1;
  const lastRiskRow =
    promptHeading > riskHeader ? promptHeading - 2 : promptHeader - 2;
  for (
    let sourceRow = riskHeader + 1;
    sourceRow <= lastRiskRow;
    sourceRow += 1
  ) {
    if (!source.getCell(sourceRow, 1).value) continue;
    const values = [
      source.getCell(sourceRow, 1),
      source.getCell(sourceRow, 2),
      source.getCell(sourceRow, 3),
      source.getCell(sourceRow, 4),
    ];
    values.forEach((sourceCell, index) => {
      target.getCell(targetRow, index + 1).value = copiedCellValue(
        sourceCell.value,
        sourceCell.result,
      ) as string | number | null;
    });
    target.getCell(targetRow, 5).value = [
      copiedCellValue(
        source.getCell(sourceRow, 6).value,
        source.getCell(sourceRow, 6).result,
      ),
      copiedCellValue(
        source.getCell(sourceRow, 7).value,
        source.getCell(sourceRow, 7).result,
      ),
      copiedCellValue(
        source.getCell(sourceRow, 8).value,
        source.getCell(sourceRow, 8).result,
      ),
      copiedCellValue(
        source.getCell(sourceRow, 9).value,
        source.getCell(sourceRow, 9).result,
      ),
    ]
      .filter(Boolean)
      .join(" — ");
    for (let column = 1; column <= 5; column += 1) {
      target.getCell(targetRow, column).alignment = {
        wrapText: true,
        vertical: "top",
      };
      target.getCell(targetRow, column).font = { name: "Arial", size: 8.5 };
    }
    target.getRow(targetRow).height = 48;
    targetRow += 1;
  }
  const actionLastRow = targetRow - 1;

  targetRow += 1;
  target.getCell(targetRow, 1).value =
    "MODEL-GENERATED DILIGENCE PROMPTS — NOT SITE FINDINGS";
  target.getCell(targetRow, 1).font = {
    name: "Arial",
    size: 10,
    bold: true,
    color: { argb: "17365D" },
  };
  target.mergeCells(targetRow, 1, targetRow, 5);
  targetRow += 1;
  target.getCell(targetRow, 1).value =
    "All prompts remain unverified; cost treatment is not separately added unless reconciled.";
  target.getCell(targetRow, 1).font = {
    name: "Arial",
    size: 8.5,
    italic: true,
    color: { argb: "5B6779" },
  };
  target.mergeCells(targetRow, 1, targetRow, 5);
  targetRow += 1;
  ["Category", "Severity", "Affected buildings", "Required evidence"].forEach(
    (label, index) => {
      const cell = target.getCell(targetRow, index + 1);
      cell.value = label;
      cell.font = {
        name: "Arial",
        size: 9,
        bold: true,
        color: { argb: "FFFFFF" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "17365D" },
      };
    },
  );
  target.mergeCells(targetRow, 4, targetRow, 5);
  targetRow += 1;

  const promptGroups = new Map<
    string,
    { severities: Set<string>; buildings: Set<string>; count: number }
  >();
  for (
    let sourceRow = promptHeader + 1;
    sourceRow <= source.rowCount;
    sourceRow += 1
  ) {
    const id = String(source.getCell(sourceRow, 1).value ?? "");
    if (!id) continue;
    const category = id.split("_")[0].toLowerCase();
    const group = promptGroups.get(category) ?? {
      severities: new Set<string>(),
      buildings: new Set<string>(),
      count: 0,
    };
    group.count += 1;
    group.severities.add(String(source.getCell(sourceRow, 2).value ?? "—"));
    const prompt = String(source.getCell(sourceRow, 3).value ?? "");
    const building = prompt.match(/— ([^:]+):/)?.[1];
    if (building) group.buildings.add(building);
    promptGroups.set(category, group);
  }

  for (const [category, group] of promptGroups) {
    const label =
      category === "hazmat"
        ? "Hazardous materials"
        : category === "bal"
          ? "Bushfire / BAL"
          : category.toUpperCase();
    const requiredEvidence =
      category === "hazmat"
        ? `${group.count} unverified prompts — obtain site-specific hazardous-materials surveys before reliance.`
        : category === "bal"
          ? `${group.count} unverified prompts — obtain a site-specific bushfire/BAL assessment before reliance.`
          : `${group.count} unverified prompts — obtain site-specific evidence before reliance.`;
    target.getCell(targetRow, 1).value = label;
    target.getCell(targetRow, 2).value = [...group.severities].join(", ");
    target.getCell(targetRow, 3).value = [...group.buildings].join(", ");
    target.getCell(targetRow, 4).value = requiredEvidence;
    target.mergeCells(targetRow, 4, targetRow, 5);
    for (let column = 1; column <= 4; column += 1) {
      target.getCell(targetRow, column).alignment = {
        wrapText: true,
        vertical: "top",
      };
      target.getCell(targetRow, column).font = { name: "Arial", size: 8.5 };
    }
    target.getRow(targetRow).height = 30;
    targetRow += 1;
  }

  [12, 26, 26, 40, 48].forEach((width, index) => {
    target.getColumn(index + 1).width = width;
  });
  return { headerRow: actionHeaderRow, lastRow: actionLastRow };
}

function compactBoardControlPanel(
  target: ExcelWorkbook["worksheets"][0] | undefined,
) {
  if (!target) return null;
  const sourceHeaderRow = findRowByFirstCell(target, "ID");
  if (sourceHeaderRow !== 4) {
    const existingHeaderRow = findRowByFirstCell(target, "ID");
    return existingHeaderRow > 0
      ? { headerRow: existingHeaderRow, lastRow: target.rowCount }
      : null;
  }

  // The working-model sheet carries a subtitle and spacer. In the two-page
  // Board issue those rows force the final two checks onto the action page.
  // Remove only those presentation rows, preserve all 18 controls, and repair
  // the four helper-cell formulas whose row references ExcelJS does not shift.
  target.unMergeCells("A2:E2");
  target.spliceRows(2, 2);
  target.getCell(1, 1).value =
    "MODEL INTEGRITY CHECKS — 18-CHECK CONTROL PANEL (PASS / REVIEW / FAIL)";

  const headerRow = sourceHeaderRow - 2;
  for (let row = headerRow + 1; row <= target.rowCount; row += 1) {
    const statusCell = target.getCell(row, 5);
    const value = statusCell.value;
    if (
      value &&
      typeof value === "object" &&
      "formula" in value &&
      typeof value.formula === "string"
    ) {
      statusCell.value = {
        ...value,
        formula: value.formula.replaceAll(`$F${row + 2}`, `$F${row}`),
      };
    }
  }
  return { headerRow, lastRow: target.rowCount };
}

function rewriteBoardReferences(wb: ExcelWorkbook) {
  const rewrite = (formula: string) => {
    let updated = formula;
    BOARD_RENAMES.forEach((replacement, original) => {
      updated = updated
        .replaceAll(`'${original}'!`, `'${replacement}'!`)
        .replaceAll(`${original}!`, `'${replacement}'!`);
    });
    return updated;
  };

  wb.eachSheet((worksheet) => {
    worksheet.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        const value = cell.value;
        if (
          !value ||
          typeof value !== "object" ||
          !("formula" in value) ||
          typeof value.formula !== "string"
        ) {
          return;
        }
        cell.value = { ...value, formula: rewrite(value.formula) };
      });
    });
  });

  const definedNames = wb.definedNames as unknown as {
    model: Array<{ name: string; ranges: string[] }>;
  };
  definedNames.model = definedNames.model.map((entry) => ({
    ...entry,
    ranges: entry.ranges.map(rewrite),
  }));
}

/**
 * Convert the calculation-rich working model into the 8+6 Board issue.
 * Evidence is copied as values before redundant tabs are removed; retained
 * calculations remain live and every cross-sheet formula is renamed.
 */
export function applyBoardWorkbookLayout(wb: ExcelWorkbook) {
  buildOperatorBoardGrid(
    wb.getWorksheet("Headline KPIs"),
    wb.getWorksheet("Op P&L"),
    wb.getWorksheet("Assumptions"),
  );
  buildRentBoardGrid(wb.getWorksheet("Rent Bridge"));
  buildAssumptionsBoardGrid(
    wb.getWorksheet("Assumptions"),
    wb.getWorksheet("Op P&L"),
  );
  appendPrimaryCharts(
    wb.getWorksheet("Headline KPIs"),
    wb.getWorksheet("Charts"),
  );
  replaceNonResponsiveTwoWaySensitivity(wb);
  buildCostBoardGrid(
    wb.getWorksheet("Cost & Yield"),
    wb.getWorksheet("Builder Quotes"),
  );
  const modelChecks = wb.getWorksheet("Model Checks");
  const controlBlock = compactBoardControlPanel(modelChecks);
  if (controlBlock) {
    addNativeTableFromRange(
      modelChecks,
      "ModelControlRegister",
      controlBlock.headerRow,
      controlBlock.lastRow,
      5,
    );
  }
  const actionItems = wb.getWorksheet("Action Items");
  const actionBlock = appendBoardActionRegister(modelChecks, actionItems);
  if (actionBlock) {
    addNativeTableFromRange(
      modelChecks,
      "BoardActionRegister",
      actionBlock.headerRow,
      actionBlock.lastRow,
      5,
    );
    if (modelChecks) {
      modelChecks.pageSetup = {
        ...modelChecks.pageSetup,
        printArea: rowAbsolutePrintArea(
          `A1:F${Math.max(actionBlock.lastRow, modelChecks.rowCount)}`,
        ),
      };
    }
  }
  appendEvidenceSheet(
    wb.getWorksheet("Building Schedule"),
    wb.getWorksheet("Buildings"),
    "RAW BUILDING INPUTS",
  );
  const developmentCashflow = appendEvidenceSheet(
    wb.getWorksheet("Dev Cost"),
    wb.getWorksheet("Dev Cashflow"),
    "DEVELOPMENT CASHFLOW",
  );
  if (developmentCashflow) {
    rewriteMovedSheetReferences(
      wb,
      "Dev Cashflow",
      "Dev Cost",
      developmentCashflow.rowOffset,
    );
  }
  appendEvidenceSheet(
    wb.getWorksheet("Debt Schedule"),
    wb.getWorksheet("Dev Drawdown"),
    "DEVELOPMENT DRAWDOWN",
  );
  appendCompactSensitivity(
    wb.getWorksheet("Two-Way Sensitivity"),
    wb.getWorksheet("Interest Sensitivity"),
    "INTEREST RATE SENSITIVITY",
    [
      { sourceColumn: 1, label: "Scenario" },
      { sourceColumn: 2, label: "Total rate" },
      { sourceColumn: 4, label: "Development interest" },
      { sourceColumn: 6, label: "Profit delta vs base" },
    ],
  );
  appendCompactSensitivity(
    wb.getWorksheet("Two-Way Sensitivity"),
    wb.getWorksheet("LVR Sensitivity"),
    "CONSTRUCTION LVR SENSITIVITY",
    [
      { sourceColumn: 1, label: "Scenario" },
      { sourceColumn: 2, label: "LVR" },
      { sourceColumn: 3, label: "Facility limit" },
      { sourceColumn: 4, label: "Equity required" },
      { sourceColumn: 5, label: "Equity %" },
    ],
  );
  buildDownsideBoardGrid(
    wb.getWorksheet("Two-Way Sensitivity"),
    wb.getWorksheet("Headline KPIs"),
  );
  const census = wb.getWorksheet("Census");
  addNativeTableFromRange(
    census,
    "CatchmentEvidenceRegister",
    2,
    census?.rowCount ?? 0,
    4,
  );
  const builderQuotes = wb.getWorksheet("Builder Quotes");
  const quoteBlock = appendEvidenceSheet(
    census,
    builderQuotes,
    "BUILDER QUOTE REGISTER",
  );
  const quoteHeader = findRowByFirstCell(builderQuotes, "Builder");
  if (quoteBlock && quoteHeader > 0) {
    addNativeTableFromRange(
      census,
      "BuilderQuoteRegister",
      quoteBlock.rowOffset + quoteHeader,
      quoteBlock.rowOffset + (builderQuotes?.rowCount ?? quoteHeader),
      10,
    );
  }
  appendEvidenceSheet(census, actionItems, "FULL ACTION & DILIGENCE REGISTER");
  const downside = wb.getWorksheet("Two-Way Sensitivity");
  if (downside) {
    [26, 16, 20, 20, 16].forEach((width, index) => {
      downside.getColumn(index + 1).width = width;
    });
  }

  BOARD_RENAMES.forEach((replacement, original) => {
    const worksheet = wb.getWorksheet(original);
    if (worksheet) worksheet.name = replacement;
  });
  rewriteBoardReferences(wb);

  const retained = new Set(BOARD_SHEET_ORDER);
  [...wb.worksheets].forEach((worksheet) => {
    if (!retained.has(worksheet.name)) wb.removeWorksheet(worksheet.id);
  });
  reorderWorksheets(wb, BOARD_SHEET_ORDER);

  const boardNames = new Set(BOARD_SHEET_ORDER.slice(0, 8));
  for (const worksheet of wb.worksheets) {
    const views =
      Array.isArray(worksheet.views) && worksheet.views.length > 0
        ? worksheet.views
        : [{}];
    worksheet.views = views.map((view) => ({
      ...view,
      showGridLines: false,
    }));
    worksheet.properties.tabColor = {
      argb: boardNames.has(worksheet.name) ? "7E6A3F" : "A6A6A6",
    };
    worksheet.state = boardNames.has(worksheet.name) ? "visible" : "hidden";
  }
}

/* ── Font normalization ── */
export function normalizeFonts(wb: ExcelWorkbook) {
  for (const ws of wb.worksheets) {
    if (typeof ws.eachRow !== "function") continue;
    ws.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        const f = cell.font ?? {};
        if (f.name === "Arial") return;
        const wasDefault = !f.name || f.name === "Calibri";
        cell.font = {
          ...f,
          name: "Arial",
          size: wasDefault && (!f.size || f.size === 11) ? 10 : f.size,
          scheme: undefined,
        };
      });
    });
  }
}

export function normalizeWorkbookFormulas(wb: ExcelWorkbook) {
  for (const ws of wb.worksheets) {
    ws.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        if (cell.isMerged && cell.master.address !== cell.address) {
          return;
        }
        const value = cell.value;
        if (
          typeof value !== "object" ||
          value === null ||
          !("formula" in value) ||
          typeof value.formula !== "string"
        ) {
          return;
        }

        const cachedResult =
          cell.result ?? ("result" in value ? value.result : undefined);
        const formula = normalizeFormulaExpression(value.formula);
        if (!formula) {
          cell.value = cachedResult === undefined ? null : cachedResult;
          return;
        }
        cell.value = {
          ...value,
          formula,
          ...(cachedResult !== undefined ? { result: cachedResult } : {}),
        };
      });
    });
  }
}

/* ── Public API ── */

/**
 * Build the workbook without triggering a download.
 * Exported so integration tests can inspect the generated workbook directly.
 */
export async function buildWorkbook(
  scenario: Scenario,
  result: ModelResult,
  allScenarios: Scenario[],
  allResults: Record<string, ModelResult>,
  opts?: { skipCharts?: boolean; boardIssueLayout?: boolean },
): Promise<ExcelWorkbook> {
  // Dated-v2 has a separate ledger-based export. Do not let migration-era
  // legacy assumptions produce a familiar-looking legacy financial workbook.
  if (scenario.calculationMethod === "dated-v2") {
    return buildDealExcelWorkbook(
      buildDealReport(allScenarios, { baselineId: scenario.id }),
    );
  }
  const wb = new Workbook();
  wb.creator = "TENURE";
  wb.lastModifiedBy = "TENURE";
  wb.company = "TENURE";
  wb.manager = "Inspired Education";
  wb.title = `${scenario.assumptions.projectName} — ${scenario.name}`;
  wb.subject = "Inspired Education Board financial assessment";
  wb.description =
    "Campus lease financial assessment prepared for Board review.";
  wb.keywords = "campus lease, Inspired Education, Board, financial model";
  wb.category = "Board financial assessment";
  wb.created = new Date();
  wb.modified = new Date();
  wb.calcProperties.fullCalcOnLoad = true;

  const refs = buildAssumptions(wb, scenario.assumptions);
  const bldgRefs = buildBuildingSchedule(
    wb,
    scenario.buildings,
    scenario.assumptions,
    refs,
  );
  const cf = buildDevCashflow(wb, result.developer, scenario.assumptions);
  const devCost = buildDevCost(
    wb,
    result.developer,
    scenario.assumptions,
    refs,
    bldgRefs,
    cf,
    result.kpis,
  );
  const opPl = buildOpPL(
    wb,
    result.years,
    scenario.assumptions,
    refs,
    bldgRefs,
    result.kpis,
    result.campus.totalReddamCapex,
  );
  const kpiRefs = buildKPIs(
    wb,
    result.kpis,
    scenario.assumptions,
    refs,
    opPl,
    devCost,
    result.years[0]?.amenityRent ?? 0,
  );
  buildCostAndYield(wb, scenario, result, refs, devCost);
  buildModelChecks(wb, scenario, result, refs, opPl, devCost);
  buildRentBridge(wb, scenario, result, refs, opPl, devCost);
  buildLeaseVsOwnSheet(wb, scenario, result, refs, opPl, devCost);
  buildCover(wb, scenario, result, refs, bldgRefs, devCost, kpiRefs, opPl);
  buildBuildings(wb, scenario.buildings);
  buildDebtSchedule(wb, scenario.assumptions, result.developer, refs, devCost);
  buildInterestSensitivity(
    wb,
    scenario.assumptions,
    result.developer,
    refs,
    devCost,
  );
  buildLVRSensitivity(
    wb,
    scenario.assumptions,
    result.developer,
    refs,
    devCost,
  );
  buildTwoWaySensitivity(wb, scenario, result);
  await buildScenarios(wb, scenario, allScenarios, allResults);

  const mr = computeMonthly(scenario, result);
  buildMonthly(wb, mr);
  buildMonthlyDev(wb, mr);
  buildCensus(wb, scenario);
  if (!opts?.skipCharts) {
    await buildChartsSheet(wb, result, scenario);
  }
  buildBuilderQuotes(wb, scenario);
  buildActionItems(wb, scenario, result);

  if (opts?.boardIssueLayout) {
    applyBoardWorkbookLayout(wb);
  }
  reorderWorksheets(wb, SHEET_ORDER);
  applyPageSetup(wb, scenario);
  normalizeFonts(wb);
  normalizeWorkbookFormulas(wb);

  return wb;
}

export async function exportExcel(
  scenario: Scenario,
  result: ModelResult,
  allScenarios: Scenario[],
  allResults: Record<string, ModelResult>,
) {
  const wb = await buildWorkbook(scenario, result, allScenarios, allResults);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName =
    `${scenario.assumptions.projectName}_${scenario.name}_model`.replace(
      /[^a-zA-Z0-9_-]/g,
      "_",
    );
  a.download = `${safeName}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
